/**
 * BUG-687…693 (owner, 2026-10-20 — «сохранить всё, кроме изменённого»): wording for
 * what changed WHILE a route build was running.
 *
 * The auto-build plans without locks and re-checks under the write lock what the plan
 * assumed. Whatever changed meanwhile is taken out of the plan and reported:
 *
 * | reason                         | detail                          |
 * |--------------------------------|---------------------------------|
 * | `order_changed_during_build`   | `{fields: ["lat", "time_window_start", …] \| ["status"]}` |
 * | `vehicle_changed_during_build` | `{vehicle_id, vehicle_name}`    |
 *
 * Two surfaces word them: the `unassigned` breakdown of a build that still wrote
 * something (via `describeUnassignedDetail`) and the `ROUTE_BUILD_STALE` refusal when
 * nothing was writable (`params: {count, orders: [{order_id, reason, detail?}]}`,
 * via {@link buildStaleBuildParams}). Both go through this module so they cannot drift.
 *
 * Lives in `lib/` because `apiErrors.ts` and the notifications slice need it and a
 * `lib` module must not import from a feature slice.
 */

import type { TranslateFn } from "@/lib/apiErrors";

/**
 * The plan fields the backend compares (`_PLAN_FIELDS` in `routes/services.py`, plus
 * `status` for an order that is no longer new), mapped to ONE wording key each.
 * Several fields share a key — `lat`+`lon` are one address, the window's start/end/
 * strictness are one window — so «координаты, координаты» never renders.
 *
 * Enumerated because the key is built dynamically (`check:i18n` cannot see it): a
 * field missing here renders as its raw name rather than a raw key path.
 */
const FIELD_KEYS: Record<string, string> = {
  lat: "location",
  lon: "location",
  delivery_date: "deliveryDate",
  delivery_type: "deliveryType",
  strict_time_window: "timeWindow",
  time_window_start: "timeWindow",
  time_window_end: "timeWindow",
  transport_requirements: "requirements",
  weight_kg: "weight",
  volume_m3: "volume",
  pallets: "pallets",
  service_time_min: "serviceTime",
  status: "status",
};

/** The raw `fields` of an `order_changed_during_build` detail, or `[]`. */
export function readChangedFields(
  detail: Record<string, unknown> | null | undefined,
): string[] {
  const raw = detail?.fields;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (field): field is string => typeof field === "string" && field !== "",
  );
}

/**
 * «адрес (координаты), окно доставки» — the changed fields in words, each once, in
 * the order the backend listed them. An unknown field is named as sent (honest,
 * greppable) rather than dropped.
 */
export function describeChangedFields(
  fields: readonly string[],
  t: TranslateFn,
): string {
  const words: string[] = [];
  for (const field of fields) {
    const key = FIELD_KEYS[field];
    const word = key ? t(`routes.buildAlert.changedFields.${key}`) : field;
    if (!words.includes(word)) words.push(word);
  }
  return words.join(", ");
}

/**
 * The worded cause of an `order_changed_during_build` order, or `null` when the
 * detail names no field. A status-only change says what that means — the order is
 * no longer new — instead of the bare word «статус».
 */
export function describeOrderChangedDetail(
  detail: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): string | null {
  const fields = readChangedFields(detail);
  if (fields.length === 0) return null;
  if (fields.length === 1 && fields[0] === "status") {
    return t("routes.buildAlert.details.orderStatusChanged");
  }
  return t("routes.buildAlert.details.orderChanged", {
    fields: describeChangedFields(fields, t),
  });
}

/** «Газель 1» — the vehicle of a `vehicle_changed_during_build` detail, or `null`. */
export function readChangedVehicleName(
  detail: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): string | null {
  if (!detail) return null;
  const name =
    typeof detail.vehicle_name === "string" ? detail.vehicle_name.trim() : "";
  if (name) return name;
  const id = Number(detail.vehicle_id);
  return Number.isInteger(id) && id > 0
    ? t("routes.buildAlert.details.routeNumber", { id })
    : null;
}

/** The worded cause of a `vehicle_changed_during_build` order, or `null`. */
export function describeVehicleChangedDetail(
  detail: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): string | null {
  const vehicle = readChangedVehicleName(detail, t);
  return vehicle
    ? t("routes.buildAlert.details.vehicleChanged", { vehicle })
    : null;
}

interface StalePick {
  reason: string;
  detail: Record<string, unknown> | null;
}

function readStalePicks(raw: unknown): StalePick[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.reason !== "string") return [];
    const detail =
      typeof record.detail === "object" &&
      record.detail !== null &&
      !Array.isArray(record.detail)
        ? (record.detail as Record<string, unknown>)
        : null;
    return [{ reason: record.reason, detail }];
  });
}

/** How many vehicles the refusal names before «…». */
const MAX_NAMED_VEHICLES = 3;

const STALE = "notifications.failureCodes.ROUTE_BUILD_STALE";

/**
 * Interpolation params for the `ROUTE_BUILD_STALE` refusal — a build none of whose
 * planned routes could be written because orders or vehicles changed meanwhile. The
 * backend sends `params: {count, orders}` (first 10 orders and their reasons); this
 * composes «Маршруты не сохранены: пока шло построение, изменили 2 заказа (окно
 * доставки, вес); заняли или изменили машину «Газель 1» (3 заказа). Запустите
 * построение ещё раз.»
 *
 * Returns `{}` (→ the figure-less base sentence) when the params are absent or name
 * no order — never «undefined», never a confident 0.
 */
export function buildStaleBuildParams(
  params: Record<string, unknown> | null | undefined,
  t: TranslateFn,
): Record<string, unknown> {
  if (!params) return {};
  const picks = readStalePicks(params.orders);
  const rawCount = Number(params.count);
  const count =
    Number.isInteger(rawCount) && rawCount > 0 ? rawCount : picks.length;
  if (picks.length === 0) return {};

  const parts: string[] = [];

  const changed = picks.filter(
    (p) => p.reason === "order_changed_during_build",
  );
  if (changed.length > 0) {
    const fields = describeChangedFields(
      changed.flatMap((p) => readChangedFields(p.detail)),
      t,
    );
    parts.push(
      t(`${STALE}.ordersChanged`, {
        count: changed.length,
        ...(fields ? { fields, context: "named" } : {}),
      }),
    );
  }

  const taken = picks.filter(
    (p) => p.reason === "vehicle_changed_during_build",
  );
  if (taken.length > 0) {
    const vehicles = [
      ...new Set(
        taken
          .map((p) => readChangedVehicleName(p.detail, t))
          .filter((name): name is string => name !== null),
      ),
    ];
    const quoted = vehicles.map((name) => t(`${STALE}.vehicleName`, { name }));
    const named =
      quoted.length > MAX_NAMED_VEHICLES
        ? `${quoted.slice(0, MAX_NAMED_VEHICLES).join(", ")}, …`
        : quoted.join(", ");
    const orders = t(`${STALE}.orders`, { count: taken.length });
    parts.push(
      vehicles.length === 0
        ? t(`${STALE}.vehiclesTakenUnnamed`, { orders })
        : t(
            vehicles.length === 1
              ? `${STALE}.vehicleTaken`
              : `${STALE}.vehiclesTaken`,
            { vehicles: named, orders },
          ),
    );
  }

  // Orders the re-plan of a shortened route could no longer fit (any other reason).
  const other = picks.length - changed.length - taken.length;
  if (other > 0) {
    parts.push(t(`${STALE}.otherReasons`, { count: other }));
  }

  // Orders past the backend's 10-item cap.
  const rest = Math.max(0, count - picks.length);
  if (rest > 0) {
    parts.push(t("routes.buildAlert.moreOrders", { count: rest }));
  }

  return { reasons: parts.join("; "), context: "figures" };
}
